import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { API } from "@/App";

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success("Se l'email è registrata, riceverai un link a breve.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore, riprova più tardi");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-950 flex" data-testid="forgot-password-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center bg-stone-900">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(180,160,120,0.06),transparent_70%)]" />
        <div className="relative z-10 text-center px-12">
          <img src="/logo-uol.png" alt="UOL Paper" className="h-20 w-auto mx-auto mb-6" />
          <p className="text-stone-500 text-lg font-light leading-relaxed max-w-md">
            Il simulatore di carte da parati per professionisti del design d&apos;interni
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <button
            onClick={() => navigate("/login")}
            className="flex items-center gap-2 text-stone-500 hover:text-white transition-colors mb-12 text-sm"
            data-testid="back-to-login-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="uppercase tracking-[0.15em] text-xs">Accedi</span>
          </button>

          <div className="lg:hidden mb-8">
            <img src="/logo-uol.png" alt="UOL Paper" className="h-12 w-auto" />
          </div>

          {sent ? (
            <div className="space-y-6" data-testid="forgot-sent-panel">
              <div className="flex items-center gap-3 text-emerald-400">
                <CheckCircle2 className="w-6 h-6" />
                <h2 className="text-2xl font-light text-white">Controlla la tua casella</h2>
              </div>
              <p className="text-stone-500 text-sm leading-relaxed">
                Se l&apos;indirizzo <span className="text-stone-300">{email}</span> è associato a un account,
                riceverai a breve un&apos;email con il link per reimpostare la password.
                Il link scadrà tra 60 minuti.
              </p>
              <p className="text-stone-600 text-xs">
                Non trovi l&apos;email? Controlla la cartella Spam o riprova tra qualche minuto.
              </p>
              <Button
                onClick={() => navigate("/login")}
                className="w-full rounded-none bg-white text-stone-950 hover:bg-stone-200 h-12 text-sm uppercase tracking-[0.15em]"
                data-testid="return-login-btn"
              >
                Torna al login
              </Button>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-light text-white mb-2">Password dimenticata?</h2>
              <p className="text-stone-500 text-sm mb-8">
                Inserisci la tua email e ti invieremo un link per reimpostarla.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-widest text-stone-500">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-600" />
                    <Input
                      data-testid="forgot-email-input"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="rounded-none bg-transparent border-white/10 text-white pl-10 h-11 focus:border-amber-300/40"
                      placeholder="mario@studio.it"
                      required
                    />
                  </div>
                </div>

                <Button
                  data-testid="forgot-submit-btn"
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-none bg-white text-stone-950 hover:bg-stone-200 h-12 text-sm uppercase tracking-[0.15em]"
                >
                  {loading ? "Invio in corso..." : "Invia link di reset"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
